// @ai-generated 2026-08-21T00:00:00Z by niklas.eicker@tngtech.com using Claude Opus 5 (1M context)
import {
    affected,
    created,
    customer,
    decimal,
    describeCriteria,
    describeIdCount,
    describeOrderCount,
    enumText,
    found,
    nestedCustomer,
    text,
} from './entityToolFormatting';
import type { EntityPage } from '@utils/toolCallEnvelope';

export interface Column {
    label: string;
    /** Reads the cell from one search result; undefined renders as an em dash. */
    value: (item: Record<string, unknown>) => string | undefined;
    numeric?: boolean;
}

export interface EntityTool {
    name: string;
    title: string;
    /** Route of one record's detail frame, relative to the mandant subpath. */
    detailPath: (id: string) => string;
    /** States what the call produced, e.g. "3 Aufträge" or "2 Rechnungen erstellt". */
    summarize: (page: EntityPage) => string;
    /** States what was asked for, while there is no result yet. */
    describeArgs: (args: unknown) => string | undefined;
    columns: Column[];
}

/** invoices:generate returns the same InvoiceSearchResult that a search returns. */
const INVOICE_COLUMNS: Column[] = [
    { label: 'Nummer', value: text('number') },
    { label: 'Typ', value: text('type') },
    { label: 'Status', value: text('processState') },
    { label: 'Kunde', value: customer },
    { label: 'Netto', value: decimal('totalNetPrice'), numeric: true },
    { label: 'Brutto', value: decimal('totalGrossPrice'), numeric: true },
];

const INVOICE_DETAIL_PATH = (id: string) => `billing/invoices/${id}`;

const ORDER_DETAIL_PATH = (id: string) => `orders/${id}`;

/** The domain order a write returns: enums as member names, customer still nested. */
const APPROVED_ORDER_COLUMNS: Column[] = [
    { label: 'Nummer', value: text('number') },
    { label: 'Typ', value: enumText('type') },
    { label: 'Status', value: enumText('processState') },
    { label: 'Kunde', value: nestedCustomer },
];

/** Same for the domain invoice: no flattened customer, enums by name. */
const APPROVED_INVOICE_COLUMNS: Column[] = [
    { label: 'Nummer', value: text('number') },
    { label: 'Typ', value: enumText('type') },
    { label: 'Status', value: enumText('processState') },
    { label: 'Kunde', value: nestedCustomer },
    { label: 'Netto', value: decimal('totalNetPrice'), numeric: true },
    { label: 'Brutto', value: decimal('totalGrossPrice'), numeric: true },
];

export const ENTITY_TOOLS: EntityTool[] = [
    {
        name: 'orders.search.v1',
        title: 'Aufträge gesucht',
        detailPath: (id) => `orders/${id}`,
        summarize: found('Aufträge'),
        describeArgs: describeCriteria,
        columns: [
            { label: 'Nummer', value: text('number') },
            { label: 'Typ', value: text('type') },
            { label: 'Status', value: text('processState') },
            { label: 'Kunde', value: customer },
        ],
    },
    {
        name: 'invoices.search.v1',
        title: 'Rechnungen gesucht',
        detailPath: INVOICE_DETAIL_PATH,
        summarize: found('Rechnungen'),
        describeArgs: describeCriteria,
        columns: INVOICE_COLUMNS,
    },
    {
        name: 'addresses.search.v1',
        title: 'Adressen gesucht',
        detailPath: (id) => `stammdaten/addresses/${id}`,
        summarize: found('Adressen'),
        describeArgs: describeCriteria,
        columns: [
            { label: 'Nummer', value: text('number') },
            { label: 'Name', value: text('label') },
            {
                label: 'Straße',
                value: (item) =>
                    [text('street')(item), text('houseNumber')(item)].filter(Boolean).join(' ') || undefined,
            },
            { label: 'PLZ', value: text('zipCode') },
            { label: 'Ort', value: text('city') },
            { label: 'E-Mail', value: text('email') },
        ],
    },
    {
        name: 'contacts.search.v1',
        title: 'Ansprechpartner gesucht',
        detailPath: (id) => `stammdaten/contacts/${id}`,
        summarize: found('Ansprechpartner'),
        describeArgs: describeCriteria,
        columns: [
            {
                label: 'Name',
                value: (item) =>
                    [text('firstName')(item), text('lastName')(item)].filter(Boolean).join(' ') || undefined,
            },
            { label: 'E-Mail', value: text('email') },
            { label: 'Telefon', value: text('phone') },
            { label: 'Mobil', value: text('mobile') },
        ],
    },
    {
        name: 'invoices.generate.v1',
        title: 'Rechnungen erstellt',
        detailPath: INVOICE_DETAIL_PATH,
        summarize: created('Rechnung', 'Rechnungen'),
        describeArgs: describeOrderCount,
        columns: INVOICE_COLUMNS,
    },
    {
        name: 'orders.approve.v1',
        title: 'Aufträge freigegeben',
        detailPath: ORDER_DETAIL_PATH,
        summarize: affected('Auftrag', 'Aufträge', 'freigegeben'),
        describeArgs: describeIdCount('orderIds', 'Auftrag', 'Aufträge'),
        columns: APPROVED_ORDER_COLUMNS,
    },
    {
        name: 'order.approve.v1',
        title: 'Auftrag freigegeben',
        detailPath: ORDER_DETAIL_PATH,
        summarize: affected('Auftrag', 'Aufträge', 'freigegeben'),
        describeArgs: describeIdCount('orderId', 'Auftrag', 'Aufträge'),
        columns: APPROVED_ORDER_COLUMNS,
    },
    {
        name: 'invoices.approve.v1',
        title: 'Rechnungen freigegeben',
        detailPath: INVOICE_DETAIL_PATH,
        summarize: affected('Rechnung', 'Rechnungen', 'freigegeben'),
        describeArgs: describeIdCount('invoiceIds', 'Rechnung', 'Rechnungen'),
        columns: APPROVED_INVOICE_COLUMNS,
    },
    {
        name: 'invoice.approve.v1',
        title: 'Rechnung freigegeben',
        detailPath: INVOICE_DETAIL_PATH,
        summarize: affected('Rechnung', 'Rechnungen', 'freigegeben'),
        describeArgs: describeIdCount('invoiceId', 'Rechnung', 'Rechnungen'),
        columns: APPROVED_INVOICE_COLUMNS,
    },
];
